import React, { useEffect, useState } from 'react'
import { Bell, X } from 'lucide-react'

const STORAGE_KEY = 'softphone.notif-banner-dismissed'

/**
 * AC-20: non-blocking banner prompting the user to enable desktop
 * notifications when Notification permission is `default` (not decided yet).
 * Dismissable; stored in localStorage so we don't nag.
 * Pastel sky tint (info severity).
 *
 * Renders IN FLOW at the top of <main>, next to UsageCapBanner — deliberately
 * not as a fixed overlay. It used to be `fixed top-0`, which painted straight
 * over the page header on mobile: `elementFromPoint` on the "Good evening"
 * heading returned this banner, and the eyebrow above it was fully covered.
 * Moving it to the bottom instead just traded that for covering the "More
 * navigation" dome. A persistent, dismissible prompt shouldn't overlay
 * anything — the bottom of a mobile screen is already crowded with the tab
 * bar, the dome, a FAB and the softphone pill. In flow it pushes content down
 * and collides with nothing at any viewport.
 */
export function NotificationBanner(): React.ReactElement | null {
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
    return Notification.permission
  })
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })

  // Re-read permission status when it changes via another event
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return
    const interval = setInterval(() => {
      setPermission(Notification.permission)
    }, 2000)
    return () => clearInterval(interval)
  }, [])

  if (permission !== 'default' || dismissed) return null

  const handleEnable = async () => {
    try {
      const result = await Notification.requestPermission()
      setPermission(result)
    } catch {
      /* user-agent refused — leave permission as-is */
    }
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1')
    } catch {
      /* ignore quota errors */
    }
    setDismissed(true)
  }

  return (
    <div className="px-3 pt-3 animate-fade-up">
      <div className="mx-auto max-w-sm">
        <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 14px',
          borderRadius: 'var(--r-md)',
          background: 'rgba(181,211,240,0.10)',
          border: '0.5px solid rgba(181,211,240,0.28)',
          boxShadow: '0 10px 30px -8px rgba(0,0,0,0.6)',
          backdropFilter: 'blur(32px) saturate(180%)',
          WebkitBackdropFilter: 'blur(32px) saturate(180%)',
        }}
      >
        {/* Icon chip */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--r-sm)',
            background: 'rgba(181,211,240,0.16)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Bell style={{ width: 15, height: 15, color: 'var(--midnight-sky)' }} />
        </div>

        {/* Message */}
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.80)', flex: 1, lineHeight: 1.4 }}>
          Enable desktop notifications so you don&apos;t miss calls.
        </p>

        {/* Enable pill */}
        <button
          type="button"
          onClick={handleEnable}
          style={{
            height: 32,
            padding: '0 12px',
            borderRadius: 'var(--r-pill)',
            border: '0.5px solid rgba(181,211,240,0.35)',
            background: 'rgba(181,211,240,0.18)',
            color: 'var(--midnight-sky)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            touchAction: 'manipulation',
            flexShrink: 0,
          }}
          onPointerDown={(e) => (e.currentTarget.style.background = 'rgba(181,211,240,0.28)')}
          onPointerUp={(e) => (e.currentTarget.style.background = 'rgba(181,211,240,0.18)')}
          onPointerLeave={(e) => (e.currentTarget.style.background = 'rgba(181,211,240,0.18)')}
        >
          Enable
        </button>

        {/* Dismiss X */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss notification prompt"
          style={{
            width: 24,
            height: 24,
            borderRadius: 'var(--r-pill)',
            border: '0.5px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.40)',
            flexShrink: 0,
          }}
        >
          <X style={{ width: 11, height: 11 }} />
        </button>
        </div>
      </div>
    </div>
  )
}

